import React from 'react';
import { LinkProps, useInRouterContext, useNavigate, useLocation } from 'react-router';
import track from 'react-tracking';
import { omit } from 'lodash';
import { debug } from '../debug';
import wrapped from '../window_wrapper';
import { inMobileApp } from '../user_context_helpers';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import classNames from 'classnames';
import { getRouteSettings } from '../route_settings';
import { ToTarget, isActive, serializeURL, parseURL, AbsoluteLocation } from '../link_parser';
import { regionalizePath } from '../url_helpers';
import { MParticleEvent } from '../elog/mparticle_types';
import { trackEvent } from '../elog/mparticle_tracker';

let alwaysClientSide = false;

/**
 * For full SPA applications, we don't need to check if
 * a given route is client side enabled, they all are.
 */
export function alwaysClientSideTransition(should: boolean) {
  alwaysClientSide = should;
}

interface TrackingProps {
  tracking?: {
    getTrackingData: () => PlainObject;
  };
}

interface ExternalProps extends LinkProps {
  to: ToTarget;
  interactionType?: string;
  entityId?: string;
  position?: number;
  trackName?: string;
  activeClassName?: string;

  // Replace the current entry in history, instead of adding
  // use this when you want to navigate, but also want to make
  // sure we don't pollute our user's history.
  //
  // e.g. We have inset pagination of reviews on the CSP page. We
  // want to allow users to paginate or link to a specific page,
  // but we also want to make sure that when they hit back, they leave
  // the CSP page and go back to the grid.
  replace?: boolean;
  clickEvent?: MParticleEvent;

  /**
   * @internal
   * Carries a ref through CoreLink's HOC stack (withUserContext → track),
   * which strip the standard `ref`. Set by the `forwardRef` wrapper from the
   * public `ref` prop, and intentionally `Omit`'d from the wrapper's public
   * type — the wrapper is the only thing that should set it.
   */
  passthroughRef?: React.ForwardedRef<HTMLAnchorElement>;
}

interface NavigationProps {
  location?: AbsoluteLocation;
  navigate?: (url: AbsoluteLocation) => void;
}

type IProps = ExternalProps & TrackingProps & NavigationProps;

/**
 * These functions will take the given route and the current routes set on the window
 * at `Reverb.Routes`, and pass them through React Router's match function. This will
 * resolve the route given the path. We can take the current window's location, check
 * to see that its route is clientSide and then compare the destination with the same
 * check.
 *
 * <Route
 *   key="feed"
 *   path="my/feed"
 *   handle={{ clientSide: true }}
 *   ...
 *
 * This function returns a promise that will resolve to an array of booleans,
 * if both are true, you can issue a client-side navigation because you're going from and
 * to a client side route. If not, just do a location.assign().
 */
export function shouldClientSideResolve(nextLocation: AbsoluteLocation, currentLocation: Partial<AbsoluteLocation>) {
  if (nextLocation.origin) {
    // An origin means it's offsite
    return false;
  }

  const toClientSide = isClientSide(nextLocation);
  const fromClientSide = isClientSide(currentLocation);

  return Promise.all([fromClientSide, toClientSide]).then(([fromC, toC]) => {
    return fromC && toC;
  });
}

async function isClientSide(location: Partial<AbsoluteLocation>): Promise<boolean> {
  if (alwaysClientSide) {
    return true;
  }

  const settings = await getRouteSettings(location);
  if (settings.length === 0) {
    return false;
  }

  // Nested routes are returned from general to specific, check the most specific one
  return settings.pop()?.clientSide ?? false;
}


const NON_PASSTHROUGH_PROPS = [
  // direct props
  'to',
  'interactionType',
  'entityId',
  'position',
  'trackName',
  'replace',
  'clickEvent',

  // from context wrappers
  'tracking',
  'navigate',
  'location',

  // <Link> or <a> props that we may modify
  'rel',
  'className',
  'activeClassName',
  'children',
  'onClick',
  'passthroughRef',
];

function passThroughProps(props: IProps) {
  return omit(props, NON_PASSTHROUGH_PROPS);
}

/**
 * <CoreLink />
 *
 * Component for ALL client and server side links, this will provide tracking and
 * smart navigation from and to server or client routes. The routes must be configured
 * correctly to have client side behavior. If not they will fallback.
 *
 * ## Feature Set
 * * Is route aware, so if you tag a route with `handle={{ clientSide: true }}`,
 *   CoreLink will perform a client side transition if you're on and going to a `clientSide` route
 *
 * * All clicks will be tracked using react-tracking. You can pass in overrides for the tracking if needed.
 *
 * * CoreLink will fallback to a normal link if you're in a WebView.
 *   This allows native apps to intercept native click events, and re-route to deep links if needed.
 *
 * * CoreLink accepts a `to` param, that can either be a full url, a relative url, or
 *   any more complex options (i.e. `{ pathname: '/foo', query: { foo: 'bar' } }`)
 *
 * * CoreLink knows when it is "active" even if its rendered outside the current router
 *   context, no need to track active state.
 *
 * * There is no need to use CoreLink in LP or Sites, just use Link.  CoreLink will correctly work in full
 *   SPA applications, but it is not needed.
 */
export const InternalCoreLink = (props: IProps) => {
  const routerContext = useInRouterContext();

  if (routerContext) {
    return <CoreLinkWithRouter {...props} />;
  }

  return <CoreLinkBase {...props} />;
};

/**
 * <CoreLinkWithRouter />
 *
 * Internal component to render a link with a router context. Needed because router hooks are only available in a <Router>.
 */
const CoreLinkWithRouter = (props: IProps): React.ReactNode => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateToLocation = async (url: AbsoluteLocation) => {
    const { ...routerURL } = url;

    if (!routerURL.pathname && location) {
      // react-router v3 treats blank pathname as '/' :-(
      routerURL.pathname = location.pathname;
    }

    const clientSide = await shouldClientSideResolve(routerURL, location);

    if (clientSide) {
      debug(`client side transition - ${JSON.stringify(url)}`);

      // Use the parsed URL which now has both query (v3 format) and search (v7 format)
      const navigateTo = {
        pathname: routerURL.pathname,
        search: routerURL.search || '',
        hash: routerURL.hash || '',
      };

      if (props.replace) {
        navigate(navigateTo, { replace: true });
      } else {
        navigate(navigateTo, undefined);
      }
    } else {
      debug(`server side transition - ${JSON.stringify(url)}`);
      wrapped.location.assign(serializeURL(url));
    }
  };

  return (
    <CoreLinkBase
      {...props}
      location={location}
      navigate={navigateToLocation}
    />
  );
};

/**
 * <CoreLinkBase />
 *
 * Internal component to render a link. See InternalCoreLink for details
 */
const CoreLinkBase = (props: IProps): React.ReactNode => {
  async function handleClick(evt: React.MouseEvent<HTMLAnchorElement>, url: AbsoluteLocation, useNativeEvent: boolean) {
    const { clickEvent, onClick, tracking } = props;

    if (clickEvent) {
      const trackingContext = tracking?.getTrackingData() || {};
      const componentName = clickEvent.componentName || trackingContext.componentName;

      trackEvent({
        ...clickEvent,
        componentName,
      });
    }

    if (onClick) { onClick(evt); }

    // If a modifier key is hit, just let the browser do its thing
    // this will allow for "normal" behavior.
    if (useNativeEvent || (evt.metaKey || evt.altKey || evt.ctrlKey)) {
      return;
    }
    evt.preventDefault();

    try {
      if (props.navigate) {
        props.navigate(url);
      } else {
        debug(`server side transition - ${JSON.stringify(url)}`);
        wrapped.location.assign(serializeURL(url));
      }
    } catch (e) {
      debug(`could not transition - ${e}`);
      window.location.assign(serializeURL(url));
    }
  }

  const url = parseURL(props.to);
  const user = useUser();

  const isOnsiteWebLink = (
    !inMobileApp(user) && // Do not render client side links in Webviews, they don't trigger transitions
    !url.origin
  );

  const useNativeEvent = !!props.target || !isOnsiteWebLink;

  if (isOnsiteWebLink && url.pathname) {
    url.pathname = regionalizePath(url.pathname, user.urlRegion);
  }

  const active = isActive(url, props.location);

  const rel = classNames(
    props.rel,
    { noopener: props.target === '_blank' && !isOnsiteWebLink },
  );

  const classes = classNames(
    props.className,
    { [props.activeClassName ?? '']: props.activeClassName && active },
  );

  const href = serializeURL(url);

  return (
    <a
      {...passThroughProps(props)}
      className={classes || undefined}
      rel={rel || undefined}
      href={href}
      onClick={(evt) => { handleClick(evt, url, useNativeEvent); }}
      ref={props.passthroughRef}
    >
      {props.children}
    </a>
  );
};
InternalCoreLink.displayName = 'CoreLink';

const WithHOCs = track({})(InternalCoreLink);

const CoreLink = React.forwardRef<HTMLAnchorElement, Omit<ExternalProps, 'passthroughRef'>>(
  (props, ref) => <WithHOCs {...props} passthroughRef={ref} />,
);
CoreLink.displayName = 'CoreLink';

export default CoreLink;
